// Field-level validation shared by every form.
// Patterns and limits are kept in sync with backend/src/common/validation-patterns.ts —
// the backend re-checks everything; this gives instant feedback under each field.

import { ApiError } from '../services/api';
import { todayISO } from './format';

export const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
export const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const CIN_PATTERN = /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;
export const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const BANK_ACCOUNT_PATTERN = /^\d{9,18}$/;
export const UPI_PATTERN = /^[a-zA-Z0-9._-]{2,256}@[a-zA-Z][a-zA-Z0-9.-]{1,63}$/;
export const PHONE_PATTERN = /^((\+91[\s-]?|0)?[6-9]\d{9}|0\d{2,4}[\s-]?\d{6,8})$/;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
export const PIN_PATTERN = /^[1-9]\d{5}$/;
export const HSN_PATTERN = /^\d{4,8}$/;
export const WEBSITE_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;
export const PERSON_NAME_PATTERN = /^[A-Za-z][A-Za-z .'-]*$/;
export const BUSINESS_NAME_PATTERN = /^(?=.*[A-Za-z0-9])[A-Za-z0-9 &.,'()/+@#:_–—-]+$/;
export const LABEL_PATTERN = /^[A-Za-z][A-Za-z0-9 &.,'()/-]*$/;
export const CITY_PATTERN = /^[A-Za-z][A-Za-z .'-]*$/;
export const UNIT_PATTERN = /^[A-Za-z][A-Za-z0-9 ./-]*$/;
export const REFERENCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 /._#-]*$/;
export const CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]*$/;
export const CATEGORY_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9 &,./()-]*$/;
export const IRN_PATTERN = /^[a-fA-F0-9]{64}$/;
export const ACK_NO_PATTERN = /^\d{6,20}$/;
export const PASSWORD_PATTERN = /^(?=.*[A-Za-z])(?=.*\d).{8,72}$/;

export const MAX_MONEY = 9_999_999_999.99;
export const MAX_QUANTITY = 9_999_999.999;
export const MAX_GST_PERCENT = 28;
export const MAX_PAYMENT_TERMS_DAYS = 365;

// GST state code (first two digits of a GSTIN) → state name.
export const GST_STATE_CODES: Record<string, string> = {
  '01': 'Jammu and Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh',
  '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi', '08': 'Rajasthan', '09': 'Uttar Pradesh',
  '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh', '13': 'Nagaland', '14': 'Manipur',
  '15': 'Mizoram', '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal',
  '20': 'Jharkhand', '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
  '26': 'Dadra and Nagar Haveli and Daman and Diu', '27': 'Maharashtra', '29': 'Karnataka', '30': 'Goa',
  '31': 'Lakshadweep', '32': 'Kerala', '33': 'Tamil Nadu', '34': 'Puducherry',
  '35': 'Andaman and Nicobar Islands', '36': 'Telangana', '37': 'Andhra Pradesh', '38': 'Ladakh',
};

export const INDIAN_STATES = Array.from(new Set(Object.values(GST_STATE_CODES))).sort();

// Every rule returns an error message, or undefined when the value is fine.
type Result = string | undefined;

const blank = (v: string | null | undefined) => v === null || v === undefined || v.trim() === '';

export function isRealDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  if (y < 2000 || y > 2100) return false;
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

interface TextOptions {
  required?: boolean;
  min?: number;
  max?: number;
  pattern?: RegExp;
  patternMessage?: string;
}

export const v = {
  required(value: string | null | undefined, label: string): Result {
    return blank(value) ? `${label} is required` : undefined;
  },

  // Generic text: required / length / optional pattern.
  text(value: string, label: string, opts: TextOptions = {}): Result {
    const s = value.trim();
    if (!s) return opts.required ? `${label} is required` : undefined;
    if (opts.min && s.length < opts.min) return `${label} must be at least ${opts.min} characters`;
    if (opts.max && s.length > opts.max) return `${label} must be ${opts.max} characters or fewer`;
    if (opts.pattern && !opts.pattern.test(s)) return opts.patternMessage ?? `${label} contains characters that are not allowed`;
    return undefined;
  },

  personName(value: string, label: string, required = false): Result {
    return v.text(value, label, {
      required, min: 2, max: 120, pattern: PERSON_NAME_PATTERN,
      patternMessage: `${label} can only contain letters, spaces and . ' -`,
    });
  },

  businessName(value: string, label: string, required = false, max = 255, min = 2): Result {
    return v.text(value, label, { required, min, max, pattern: BUSINESS_NAME_PATTERN });
  },

  label(value: string, label: string, required = false, max = 100): Result {
    return v.text(value, label, {
      required, min: 2, max, pattern: LABEL_PATTERN,
      patternMessage: `${label} must start with a letter and use only letters, numbers and basic punctuation`,
    });
  },

  email(value: string, required = false): Result {
    const s = value.trim();
    if (!s) return required ? 'Email is required' : undefined;
    if (s.length > 255) return 'Email must be 255 characters or fewer';
    return EMAIL_PATTERN.test(s) ? undefined : 'Enter a valid email address';
  },

  phone(value: string, required = false, label = 'Phone'): Result {
    const s = value.trim();
    if (!s) return required ? `${label} is required` : undefined;
    return PHONE_PATTERN.test(s) ? undefined : 'Enter a valid 10-digit mobile number (or landline with STD code)';
  },

  gstin(value: string, required = false): Result {
    if (!value) return required ? 'GSTIN is required' : undefined;
    if (value.length !== 15) return `GSTIN must be 15 characters (you entered ${value.length})`;
    if (!GSTIN_PATTERN.test(value)) return 'Enter a valid GSTIN (e.g. 27AAAAA0000A1Z5)';
    if (!GST_STATE_CODES[value.slice(0, 2)]) return 'GSTIN starts with an unknown state code';
    return undefined;
  },

  pan(value: string, required = false): Result {
    if (!value) return required ? 'PAN is required' : undefined;
    if (value.length !== 10) return `PAN must be 10 characters (you entered ${value.length})`;
    return PAN_PATTERN.test(value) ? undefined : 'Enter a valid PAN (e.g. AAAAA0000A)';
  },

  // PAN must equal characters 3-12 of the GSTIN when both are present.
  panMatchesGstin(pan: string, gstin: string): Result {
    if (!pan || !gstin || !GSTIN_PATTERN.test(gstin) || !PAN_PATTERN.test(pan)) return undefined;
    return gstin.slice(2, 12) === pan ? undefined : `PAN does not match the GSTIN (expected ${gstin.slice(2, 12)})`;
  },

  stateMatchesGstin(state: string, gstin: string): Result {
    if (!state || !gstin || !GSTIN_PATTERN.test(gstin)) return undefined;
    const expected = GST_STATE_CODES[gstin.slice(0, 2)];
    return !expected || expected === state ? undefined : `State does not match the GSTIN (${gstin.slice(0, 2)} = ${expected})`;
  },

  cin(value: string): Result {
    if (!value) return undefined;
    return CIN_PATTERN.test(value) ? undefined : 'Enter a valid 21-character CIN (e.g. U72900MH2024PTC123456)';
  },

  pin(value: string, required = false): Result {
    if (!value) return required ? 'PIN code is required' : undefined;
    return PIN_PATTERN.test(value) ? undefined : 'PIN code must be 6 digits and cannot start with 0';
  },

  ifsc(value: string, required = false): Result {
    if (!value) return required ? 'IFSC is required' : undefined;
    return IFSC_PATTERN.test(value) ? undefined : 'Enter a valid 11-character IFSC (e.g. HDFC0001234)';
  },

  bankAccount(value: string, required = false): Result {
    if (!value) return required ? 'Account number is required' : undefined;
    return BANK_ACCOUNT_PATTERN.test(value) ? undefined : 'Account number must be 9-18 digits';
  },

  upi(value: string): Result {
    const s = value.trim();
    if (!s) return undefined;
    return UPI_PATTERN.test(s) ? undefined : 'Enter a valid UPI ID (e.g. asgenix@hdfcbank)';
  },

  website(value: string): Result {
    const s = value.trim();
    if (!s) return undefined;
    if (s.length > 255) return 'Website must be 255 characters or fewer';
    return WEBSITE_PATTERN.test(s) ? undefined : 'Enter a valid website (e.g. www.asgenix.com)';
  },

  hsn(value: string, required = true): Result {
    if (!value) return required ? 'HSN/SAC is required' : undefined;
    return HSN_PATTERN.test(value) ? undefined : 'HSN/SAC must be 4-8 digits';
  },

  unit(value: string): Result {
    return v.text(value, 'Unit', {
      required: true, max: 30, pattern: UNIT_PATTERN,
      patternMessage: 'Unit must start with a letter and use only letters, numbers and . / -',
    });
  },

  reference(value: string, label = 'Reference'): Result {
    return v.text(value, label, {
      max: 100, pattern: REFERENCE_PATTERN,
      patternMessage: `${label} can only contain letters, numbers and / . _ # -`,
    });
  },

  // Money with up to 2 decimals. `min` defaults to "greater than 0".
  money(value: string, label: string, opts: { required?: boolean; allowZero?: boolean; max?: number } = {}): Result {
    if (blank(value)) return opts.required === false ? undefined : `${label} is required`;
    if (!/^\d+(\.\d{1,2})?$/.test(value.trim())) return `${label} must be a number with up to 2 decimals`;
    const n = Number(value);
    if (!opts.allowZero && n <= 0) return `${label} must be greater than 0`;
    if (n > (opts.max ?? MAX_MONEY)) return `${label} is too large`;
    return undefined;
  },

  quantity(value: string, label = 'Quantity'): Result {
    if (blank(value)) return `${label} is required`;
    if (!/^\d+(\.\d{1,3})?$/.test(value.trim())) return `${label} must be a number with up to 3 decimals`;
    const n = Number(value);
    if (n <= 0) return `${label} must be greater than 0`;
    if (n > MAX_QUANTITY) return `${label} is too large`;
    return undefined;
  },

  gstPercent(value: string, label = 'GST %'): Result {
    if (blank(value)) return `${label} is required`;
    if (!/^\d+(\.\d{1,2})?$/.test(value.trim())) return `${label} must be a number with up to 2 decimals`;
    return Number(value) > MAX_GST_PERCENT ? `${label} cannot exceed ${MAX_GST_PERCENT}` : undefined;
  },

  wholeNumber(value: string, label: string, opts: { required?: boolean; min?: number; max?: number } = {}): Result {
    if (blank(value)) return opts.required ? `${label} is required` : undefined;
    if (!/^\d+$/.test(value.trim())) return `${label} must be a whole number`;
    const n = Number(value);
    if (opts.min !== undefined && n < opts.min) return `${label} must be at least ${opts.min}`;
    if (opts.max !== undefined && n > opts.max) return `${label} cannot exceed ${opts.max}`;
    return undefined;
  },

  date(
    value: string,
    label: string,
    opts: { required?: boolean; notFuture?: boolean; min?: string; minLabel?: string; max?: string; maxLabel?: string } = {},
  ): Result {
    if (!value) return opts.required === false ? undefined : `${label} is required`;
    if (!isRealDate(value)) return `${label} is not a valid date`;
    if (opts.notFuture && value > todayISO()) return `${label} cannot be in the future`;
    if (opts.min && value < opts.min) return `${label} cannot be before ${opts.minLabel ?? opts.min}`;
    if (opts.max && value > opts.max) return `${label} cannot be after ${opts.maxLabel ?? opts.max}`;
    return undefined;
  },

  month(value: string, label: string, opts: { notFuture?: boolean } = {}): Result {
    if (!value) return `${label} is required`;
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) return `${label} is not a valid month`;
    const y = Number(value.slice(0, 4));
    if (y < 2000 || y > 2100) return `${label} is not a valid month`;
    if (opts.notFuture && value > todayISO().slice(0, 7)) return `${label} cannot be in the future`;
    return undefined;
  },

  password(value: string, label = 'Password'): Result {
    if (!value) return `${label} is required`;
    if (value.length < 8) return `${label} must be at least 8 characters`;
    if (value.length > 72) return `${label} must be 72 characters or fewer`;
    if (!PASSWORD_PATTERN.test(value)) return `${label} must include at least one letter and one number`;
    return undefined;
  },

  // GST on a bill can be at most 28% of the base amount.
  taxWithinLimit(amount: string, tax: string): Result {
    const a = Number(amount);
    const t = Number(tax);
    if (!tax || isNaN(a) || isNaN(t) || a <= 0) return undefined;
    return t > a * 0.28 + 0.01 ? 'GST cannot be more than 28% of the amount' : undefined;
  },
};

// Drops the undefined entries so `Object.keys(errors).length === 0` means valid.
export function compact<T extends Record<string, string | undefined>>(errors: T): Partial<Record<keyof T, string>> {
  const out: Partial<Record<keyof T, string>> = {};
  (Object.keys(errors) as (keyof T)[]).forEach((k) => {
    if (errors[k]) out[k] = errors[k];
  });
  return out;
}

export const hasErrors = (errors: object) => Object.keys(errors).length > 0;

// Field errors returned by the API (keyed by DTO property), limited to the
// fields this form knows about. Lets a server-side rejection show up under
// the right input instead of only in a toast.
export function serverFieldErrors<K extends string>(err: unknown, fields: readonly K[]): Partial<Record<K, string>> {
  const fe = (err as ApiError)?.fieldErrors;
  const out: Partial<Record<K, string>> = {};
  if (!fe) return out;
  for (const f of fields) if (fe[f]) out[f] = fe[f];
  return out;
}

// Scrolls to and focuses the first field marked invalid.
export function focusFirstError(container?: HTMLElement | null) {
  window.setTimeout(() => {
    const el = (container ?? document).querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
  }, 0);
}
